import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Play, Pause, ChevronRight, Shirt } from "lucide-react";
import { SHOP_URL } from "@/lib/config";

const lineReveal = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
  }),
};

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, delay: 0.7 + i * 0.15, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = ({ onWatch }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const { scrollY } = useScroll();
  const portraitY = useTransform(scrollY, [0, 700], [0, 80]);
  const crestY = useTransform(scrollY, [0, 700], [0, 34]);

  const toggleAudio = () => {
    if (!audioRef.current) {
      audioRef.current = new Audio("/audio/sea-intro.wav");
      audioRef.current.loop = true;
    }
    if (playing) {
      audioRef.current.pause();
      setPlaying(false);
    } else {
      audioRef.current.play();
      setPlaying(true);
    }
  };

  return (
    <section className="relative grain overflow-hidden pt-28 md:pt-36 pb-16" data-testid="hero-section">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_45%_at_50%_-5%,rgba(212,175,55,0.14),transparent_70%)]" />
      <div className="relative max-w-7xl mx-auto px-5 md:px-8 text-center">
        <div className="overflow-hidden">
          <motion.p variants={lineReveal} custom={0} initial="hidden" animate="show"
            className="text-[11px] md:text-xs tracking-[0.55em] uppercase text-[#94A3B8]" data-testid="hero-eyebrow">
            Follow, Join, and Apply for the Adventure.
          </motion.p>
        </div>
        <h1 className="overflow-hidden mt-3" data-testid="hero-title">
          <motion.span variants={lineReveal} custom={1} initial="hidden" animate="show"
            className="block font-display font-bold text-5xl sm:text-6xl lg:text-8xl tracking-[0.06em] gold-text leading-none">
            CAPTAIN'S CUT<span className="align-super text-lg md:text-2xl tracking-normal">TM</span>
          </motion.span>
        </h1>

        <div className="mt-10 md:mt-14 grid lg:grid-cols-[1fr_auto_1fr] items-center gap-10 lg:gap-6">
          <motion.div style={{ y: crestY }} variants={fade} custom={0} initial="hidden" animate="show" className="order-2 lg:order-1">
            <img src="/assets/crest-show.png" alt="Captain's Cut — The Show crest"
              className="mx-auto h-52 md:h-72 object-contain mix-blend-screen" data-testid="crest-show-img" />
            <p className="font-display text-sm tracking-[0.3em] text-[#F5F3EF] mt-4">THE SHOW</p>
            <p className="font-editorial italic text-lg text-[#E8C15A] mt-1">"Can You Make the Cut?"</p>
            <p className="text-xs text-[#94A3B8] mt-2">Real maritime skills meet real challenges.</p>
          </motion.div>

          <motion.div style={{ y: portraitY }} variants={fade} custom={1} initial="hidden" animate="show"
            className="order-1 lg:order-2 relative inline-block mx-auto" data-testid="captain-portrait">
            {playing && (
              <>
                <span className="sonar-ring absolute inset-0 rounded-full border border-[#D4AF37]/60" />
                <span className="sonar-ring absolute inset-0 rounded-full border border-[#D4AF37]/40" style={{ animationDelay: "0.8s" }} />
              </>
            )}
            <img src="/assets/captain.png" alt="The Captain at the helm"
              className="h-[380px] md:h-[500px] w-auto object-cover rounded-sm shadow-[0_0_80px_rgba(212,175,55,0.18)]" />
            <button
              onClick={toggleAudio}
              aria-label={playing ? "Pause captain's audio" : "Play captain's audio"}
              className="absolute inset-0 m-auto h-20 w-20 rounded-full bg-[#D4AF37] text-[#070A0F] flex items-center justify-center shadow-[0_0_40px_rgba(212,175,55,0.55)] hover:scale-110 transition-transform duration-300"
              data-testid="hero-audio-toggle"
            >
              {playing ? <Pause size={30} fill="currentColor" /> : <Play size={30} fill="currentColor" className="ml-1" />}
            </button>
            {playing && (
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                className="absolute -bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-3 bg-[#0B111B]/90 border border-[#D4AF37]/40 px-4 py-2 backdrop-blur-md"
                data-testid="hero-audio-badge">
                <div className="flex items-end gap-[3px] h-4">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <span key={i} className="eq-bar w-[3px] h-4 bg-[#E8C15A]" style={{ animationDelay: `${i * 0.13}s` }} />
                  ))}
                </div>
                <span className="font-coords text-[10px] tracking-[0.25em] text-[#E8C15A]">CAPTAIN'S LOG · TRANSMISSION LIVE</span>
              </motion.div>
            )}
          </motion.div>

          <motion.div style={{ y: crestY }} variants={fade} custom={2} initial="hidden" animate="show" className="order-3">
            <img src="/assets/crest-apparel.png" alt="Captain's Cut Apparel anchor crest"
              className="mx-auto h-52 md:h-72 object-contain mix-blend-screen" data-testid="crest-apparel-img" />
            <p className="font-display text-sm tracking-[0.3em] text-[#F5F3EF] mt-4">APPAREL</p>
            <p className="font-editorial italic text-lg text-[#E8C15A] mt-1">Maritime Apparel</p>
            <p className="text-xs text-[#94A3B8] mt-2">for Maritime Crew.</p>
          </motion.div>
        </div>

        <motion.p variants={fade} custom={3} initial="hidden" animate="show"
          className="shimmer-text font-editorial italic text-3xl md:text-5xl mt-14" data-testid="hero-script">
          Do you have the skills to make the cut?
        </motion.p>

        <motion.div variants={fade} custom={4} initial="hidden" animate="show"
          className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
          <button onClick={onWatch}
            className="group flex items-center gap-3 bg-[#D4AF37] text-[#070A0F] font-display text-sm tracking-[0.2em] px-8 py-4 hover:bg-[#F3E08A] transition-colors duration-300"
            data-testid="hero-watch-show-btn">
            <Play size={16} fill="currentColor" /> WATCH THE SHOW
            <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
          </button>
          <a href={SHOP_URL} target="_blank" rel="noopener noreferrer"
            className="group flex items-center gap-3 border border-[#D4AF37]/50 text-[#E8C15A] font-display text-sm tracking-[0.2em] px-8 py-4 hover:bg-[#D4AF37]/10 transition-colors duration-300"
            data-testid="hero-shop-apparel-btn">
            <Shirt size={16} /> SHOP APPAREL
            <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};
